import { useCallback, useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

import { useSocket } from "../../providers/socket/socketProvider";
import { useStore } from "../../store";
import { Service } from "../../models/Service";
import { getSettings } from "../../services/organizationServices";
import { normalizeService } from "../../services/messageAdapters";
import {
  buildIncomingMessageNotification,
  getLatestIncomingContactMessage,
  getMessageNotificationTypePreferences,
  MessageNotificationTypePreferences,
  playNotificationSound,
  requestSystemNotificationPermission,
  serviceBelongsToUser,
  showSystemNotification,
} from "../../utils/messageNotifications";
import { getAssignedRole } from "../../services/accessControl";

const getRouteChatId = ( pathname: string ) => {
  const chatMatch = matchPath( "/chat/:chatId", pathname );
  return chatMatch?.params.chatId;
};

export const MessageNotificationListener = () => {
  const navigate = useNavigate();

  const {
    pathname,
  } = useLocation();

  const {
    user,
  } = useStore();
  const assignedRole = getAssignedRole( user );

  const {
    socket,
  } = useSocket();

  const [ messageNotificationsEnabled, setMessageNotificationsEnabled ] = useState<boolean>( false );

  const messageNotificationsEnabledRef = useRef<boolean>( false );
  const messageNotificationTypesRef = useRef<MessageNotificationTypePreferences>( getMessageNotificationTypePreferences( user?.id ));
  const notificationPermissionRequestedRef = useRef<boolean>( false );
  const notifiedMessageIdsRef = useRef<Set<string>>( new Set());
  const currentChatIdRef = useRef<string | undefined>( getRouteChatId( pathname ));

  const notifyIncomingMessage = useCallback(( service: Service ) => {
    if( !messageNotificationsEnabledRef.current ) return;
    if( !serviceBelongsToUser( service, user?.id )) return;

    const incomingMessage = getLatestIncomingContactMessage( service );
    if( !incomingMessage || notifiedMessageIdsRef.current.has( incomingMessage.id )) return;

    const notificationTypes = messageNotificationTypesRef.current;
    if( !notificationTypes.system && !notificationTypes.sound ) return;

    const chatId = service.chat?.id || incomingMessage.chatId;
    const currentChatIsVisible = currentChatIdRef.current === service.chat?.id && typeof document !== "undefined" && !document.hidden;
    if( currentChatIsVisible ) return;

    notifiedMessageIdsRef.current.add( incomingMessage.id );
    if( notifiedMessageIdsRef.current.size > 100 ) {
      const oldestMessageId = notifiedMessageIdsRef.current.values().next().value;
      if( oldestMessageId ) notifiedMessageIdsRef.current.delete( oldestMessageId );
    }

    if( notificationTypes.sound ) playNotificationSound();
    if( notificationTypes.system ) {
      showSystemNotification({
        ...buildIncomingMessageNotification( service, incomingMessage ),
        onClick: chatId ? () => navigate( `/chat/${ chatId }`) : undefined,
      });
    }
  }, [ navigate, user?.id ]);

  useEffect(() => {
    currentChatIdRef.current = getRouteChatId( pathname );
  }, [ pathname ]);

  useEffect(() => {
    messageNotificationsEnabledRef.current = messageNotificationsEnabled;
  }, [ messageNotificationsEnabled ]);

  useEffect(() => {
    messageNotificationTypesRef.current = getMessageNotificationTypePreferences( user?.id );
  }, [ user?.id ]);

  useEffect(() => {
    let mounted = true;

    void (async () => {
      const response = await getSettings();

      if( mounted ) setMessageNotificationsEnabled( Boolean( response.data && response.data.messageNotificationsEnabled ));
    })();

    return () => {
      mounted = false;
    };
  }, [ user?.organization.id ]);

  useEffect(() => {
    if( !messageNotificationsEnabled || !messageNotificationTypesRef.current.system ) {
      notificationPermissionRequestedRef.current = false;
      return;
    }

    if( notificationPermissionRequestedRef.current ) return;
    notificationPermissionRequestedRef.current = true;

    void requestSystemNotificationPermission();
  }, [ messageNotificationsEnabled ]);

  useEffect(() => {
    if( !socket ) return;

    const handleNewMessage = ({ service }: { service?: Service }) => {
      if( !service ) return;

      notifyIncomingMessage( normalizeService( service, assignedRole ));
    };

    socket.on( "channel:new-message", handleNewMessage );

    return () => {
      socket.off( "channel:new-message", handleNewMessage );
    };
  }, [ socket, notifyIncomingMessage, assignedRole ]);

  return null;
};
